import { useMemo, useState } from 'react';
import { Plus, TriangleAlert, ClipboardList, Clock, X, ChevronUp, Users } from 'lucide-react';
import {
  evaluate,
  loadConfig,
  elapsedSec,
  formatElapsed,
  STAGE_LABELS,
  INCIDENT_LEVEL_LABELS,
  INCIDENT_TYPE_LABELS,
  activeIncidentTypes,
  lifeUnresolved,
  computeContention,
  type Incident,
} from '@duty/shared';
import { useIncidentStore } from '@/incident/store';
import { EmptyState } from '@/components/EmptyState';

/**
 * Deterministic default config, not the active incident's live/resolved
 * doctrine config — this is a lightweight summary badge across every open
 * incident, not a replica of any one incident's own insight panel, so a
 * shared default is the right cost/accuracy trade-off (avoids a resolved-
 * config fetch per card just to render the dashboard).
 */
const config = loadConfig();

interface CardSummary {
  incident: Incident;
  criticalCount: number;
  highCount: number;
  lifeRisk: boolean;
  supportGaps: number;
  urgency: number;
}

function summarise(incident: Incident): CardSummary {
  const insights = evaluate({ incident, config, nowMs: Date.now() });
  const open = insights.filter((i) => i.status === 'open');
  const criticalCount = open.filter((i) => i.severity === 'critical').length;
  const highCount = open.filter((i) => i.severity === 'high').length;
  const lifeRisk = lifeUnresolved(incident);
  const supportGaps = incident.itasc.support.unresolvedGaps.length;
  return {
    incident,
    criticalCount,
    highCount,
    lifeRisk,
    supportGaps,
    urgency: criticalCount * 100 + highCount * 10 + supportGaps * 3 + (lifeRisk ? 5 : 0),
  };
}

/**
 * Portfolio landing screen — replaces the plain `EmptyState` once more than
 * one incident is open. Reuses `EmptyState` unmodified for the zero- and
 * "create another"-incident cases, so the first-time experience is
 * unchanged and there's only one place that knows how to build a new
 * incident.
 */
export function IncidentDashboard() {
  const { incidentList, switchToIncident, removeIncident } = useIncidentStore();
  const [showCreate, setShowCreate] = useState(false);

  const summaries = useMemo(
    () => incidentList.map(summarise).sort((a, b) => b.urgency - a.urgency || b.incident.createdAt.localeCompare(a.incident.createdAt)),
    [incidentList],
  );
  const contentionFlags = useMemo(() => computeContention(incidentList), [incidentList]);

  if (incidentList.length === 0) return <EmptyState />;

  const incidentName = (id: string) => incidentList.find((i) => i.id === id)?.name ?? 'another incident';

  return (
    <div className="mx-auto w-full max-w-2xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-1.5 text-lg font-semibold">
            <ClipboardList size={18} className="text-accent" /> Open incidents
          </h1>
          <p className="text-xs text-muted">{incidentList.length} open — most urgent first.</p>
        </div>
        <button
          onClick={() => setShowCreate((s) => !s)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-muted hover:border-accent/50 hover:text-accent"
        >
          {showCreate ? <ChevronUp size={14} /> : <Plus size={14} />} New incident
        </button>
      </div>

      {contentionFlags.length > 0 && (
        <div className="mb-4 space-y-1.5 rounded-lg border border-high/40 bg-high/10 p-3 text-xs">
          {contentionFlags.map((f) => (
            <div key={f.stationId} className="flex items-start gap-1.5 text-high">
              <Users size={13} className="mt-0.5 shrink-0" />
              <span>
                <strong>{f.stationName}</strong> is committed to more than one open incident —{' '}
                {f.incidentIds.map((id, i) => (
                  <span key={id}>
                    {i > 0 && ', '}
                    {incidentName(id)}
                  </span>
                ))}
                . Consider whether that brigade can actually cover both.
              </span>
            </div>
          ))}
        </div>
      )}

      <ul className="space-y-2">
        {summaries.map((s) => (
          <IncidentCard
            key={s.incident.id}
            summary={s}
            onOpen={() => switchToIncident(s.incident.id)}
            onRemove={() => {
              if (confirm(`Remove "${s.incident.name}" from the list? This can't be undone.`)) removeIncident(s.incident.id);
            }}
          />
        ))}
      </ul>

      {showCreate && (
        <div className="mt-6 border-t border-border pt-2">
          <EmptyState />
        </div>
      )}
    </div>
  );
}

function IncidentCard({ summary, onOpen, onRemove }: { summary: CardSummary; onOpen: () => void; onRemove: () => void }) {
  const { incident, criticalCount, highCount, lifeRisk, supportGaps } = summary;
  const elapsed = formatElapsed(elapsedSec(incident.clock, Date.now()));
  const typeLabel = activeIncidentTypes(incident).map((t) => INCIDENT_TYPE_LABELS[t]).join(' + ');

  return (
    <li className="panel flex items-center gap-3 p-3">
      <button onClick={onOpen} className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-medium">{incident.name}</span>
          <span className="chip">{typeLabel}</span>
          <span className="chip">{INCIDENT_LEVEL_LABELS[incident.level]}</span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
          <span>{STAGE_LABELS[incident.stage]}</span>
          <span className="flex items-center gap-1">
            <Clock size={11} /> {elapsed}
          </span>
          {lifeRisk && (
            <span className="flex items-center gap-1 text-crit">
              <TriangleAlert size={11} /> Life risk unresolved
            </span>
          )}
          {supportGaps > 0 && (
            <span className="text-high">
              {supportGaps} outstanding support {supportGaps === 1 ? 'request' : 'requests'}
            </span>
          )}
          {(criticalCount > 0 || highCount > 0) && (
            <span className="text-high">
              {criticalCount > 0 && `${criticalCount} critical`}
              {criticalCount > 0 && highCount > 0 && ' · '}
              {highCount > 0 && `${highCount} high`}
            </span>
          )}
        </div>
      </button>
      <button
        onClick={onRemove}
        aria-label={`Remove ${incident.name}`}
        title="Remove from list"
        className="shrink-0 rounded-md p-1.5 text-muted hover:text-crit"
      >
        <X size={14} />
      </button>
    </li>
  );
}
